import { memo, useCallback, useEffect, useState } from 'react';
import { ActionIcon, Box } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';

import type { EnvironmentalDataFormData } from '../../../types/reports.js';
import { SectionToggleRow } from '../../ui/SectionToggleRow.js';
import { ALL_EMISSION_FIELD_KEYS, KilnForm } from './KilnForm.js';

type EnvForm = UseFormReturnType<EnvironmentalDataFormData>;

interface KilnMeasurementCardProps {
  form: EnvForm;
  kilnIdx: number;
  measurementIdx: number;
  expanded: boolean;
  onToggle: () => void;
  /**
   * Phase 28 (D-26): optional -- `DrehofenSection` uebergibt `onDelete` nur, wenn der Ofen
   * `plantKiln.allowsOwnMeasurements` traegt. Ohne das Recht faellt der Button ganz aus dem DOM
   * (nicht nur `disabled`), damit kein Knopf eine Aktion behauptet, die es an diesem Ofen nicht
   * gibt. Die Werte bleiben in jedem Fall bearbeitbar -- deren `disabled` haengt weiterhin
   * ausschliesslich am Report-Status.
   */
  onDelete?: () => void;
  disabled?: boolean;
  errorSig: string;
}

// Fehler-Signatur einer Messung, Vorbild rowErrorSig in BrennstoffeSection.tsx: verkettet die
// Backend-Fehlermeldungen ALLER 55 Feldpfade einer Messung (measurementDate, oxygenReference,
// valueBasis + je 26 `<key>Value`- und `<key>BelowLimit`-Pfade aus ALL_EMISSION_FIELD_KEYS) zu
// einem String. Dient AUSSCHLIESSLICH als Re-Render-Ausloeser fuer die memoisierte Karte: beim
// Tippen aendert sich form.errors nicht (runValidation laeuft nur onBlur) -> Signatur stabil ->
// memo ueberspringt das Re-Render der Karte. Beim Blur setzt runValidation die Fehler -> Signatur
// dieser einen Messung aendert sich -> nur diese Karte re-rendert und die betroffene
// ValidatedNumberInput zeigt rot (form.getInputProps liest form.errors bei jedem eigenen Render
// frisch, siehe validatedFields.tsx). Die BelowLimit-Pfade tragen heute keine eigene
// Backend-Validierung, gehoeren aber in die Signatur, weil sie Teil des angezeigten
// Formularzustands sind und ein spaeterer Backend-Fehler auf ihnen sonst unsichtbar bliebe --
// exakt die T-19-27/T-19-28-Lehre. Fehlt ein Pfad, bleibt ein Backend-Fehler auf genau diesem
// Feld unsichtbar -- deshalb wird der Katalog aus KilnForm.tsx importiert statt hier ein zweites
// Mal aufgezaehlt.
export function measurementErrorSig(errors: Record<string, unknown>, prefix: string): string {
  const paths = [
    `${prefix}.measurementDate`,
    `${prefix}.oxygenReference`,
    `${prefix}.valueBasis`,
    ...ALL_EMISSION_FIELD_KEYS.flatMap((key) => [
      `${prefix}.${key}Value`,
      `${prefix}.${key}BelowLimit`,
    ]),
  ];
  return paths.map((p) => String(errors[p] ?? '')).join('|');
}

// "YYYY-MM-DD" (Mantine-DateInput-Speicherformat, lexikographisch sortierbar, D-10) wird fuer die
// Titelanzeige (D-08) nach "DD.MM.YYYY" umformatiert -- reines String-Splitting, kein Date-Objekt.
//
// 19-10-Gap-Fix (D-08): defensive Zusatzabsicherung neben dem Zuschnitt in hydrate.ts -- ein
// voller ISO-Timestamp ("2026-01-01T00:00:00+01:00") wird vor dem Splitten auf die ersten 10
// Zeichen gekuerzt, damit diese Funktion nie wieder auf ein ungekuerztes Backend-Format hereinfaellt
// (siehe 19-08-SUMMARY.md GAP 2: der Titel zerlegte den vollen Timestamp per '-'.split, sodass
// "01T00:00:00+01:00" als Tag im Titel landete).
export function formatMeasurementDateForTitle(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (!year || !month || !day) return iso;
  return `${day}.${month}.${year}`;
}

interface MeasurementTitleProps {
  form: EnvForm;
  kilnIdx: number;
  measurementIdx: number;
}

/**
 * Isolierte Blattkomponente (Muster NotesBadge.tsx, D-19): abonniert measurementDate per eigenem
 * form.watch + eigenem useCallback-stabilen Handler und haelt den Wert in lokalem State. Dadurch
 * rendert beim Tippen in eines der 26 Messfelder weder dieser Titel noch die umgebende
 * Ofen-/Formular-Ebene neu -- nur dieser Titel liest ueberhaupt measurementDate.
 *
 * form.watch gehoert direkt in den Render-Body (nicht in ein eigenes useEffect): Mantine ruft
 * intern selbst useEffect auf und meldet den Watcher im Cleanup wieder ab (Quellcode-verifiziert,
 * siehe NotesBadge-Kommentar). Ein zusaetzliches useEffect drumherum waere ein Hook-Aufruf in
 * einem Callback und stuerzt zur Laufzeit mit "Invalid hook call" ab -- NICHT zu einem useEffect
 * "reparieren".
 */
function MeasurementTitle({ form, kilnIdx, measurementIdx }: MeasurementTitleProps) {
  const dateField = `rotaryKilns.${kilnIdx}.measurements.${measurementIdx}.measurementDate`;

  const [measurementDate, setMeasurementDate] = useState<string | null>(
    () =>
      form.getValues().rotaryKilns?.[kilnIdx]?.measurements?.[measurementIdx]?.measurementDate ??
      null,
  );

  // FormPathValue<TValues, TField> faellt fuer einen zur Laufzeit zusammengesetzten (nicht
  // literalen) Pfad auf "unknown" zurueck -- der Subscriber-Parameter ist deshalb bewusst
  // `unknown` getippt und wird hier auf den erwarteten String-Typ verengt.
  const handleDateChange = useCallback(({ value }: { value: unknown }) => {
    setMeasurementDate((value as string | null | undefined) ?? null);
  }, []);

  form.watch(dateField, handleDateChange);

  // D-04/D-09: ohne Datum bleibt der Titel exakt "Messung {n}"; mit Datum
  // "Messung {n} — {DD.MM.YYYY}". Die Messart entfaellt ersatzlos aus dem Titel (D-04) -- mit der
  // Trennung ist jede Einzelmessung per Definition diskontinuierlich.
  if (!measurementDate) {
    return <>{`Messung ${measurementIdx + 1}`}</>;
  }

  return <>{`Messung ${measurementIdx + 1} — ${formatMeasurementDateForTitle(measurementDate)}`}</>;
}

/**
 * Zweite Akkordeon-Ebene (D-08/D-09/D-11/D-12): eine weisse Sub-Card je Messung innerhalb des
 * grauen Messungs-Akkordeons einer DrehofenSection-Zeile. Der Loeschbutton wird ausdruecklich
 * NICHT deaktiviert, wenn es die letzte Messung des Ofens ist (D-16) -- ein Ofen darf ohne jede
 * Messung existieren. Es gibt keinen Kopier-Button fuer eine bestehende Messung (D-17): jede
 * neue Messung entsteht ausschliesslich ueber DrehofenSection per createEmptyMeasurement.
 *
 * D-19 Fall B (Lazy-Rendering, 19-07-SUMMARY.md): der Prod-Build-Messfall (1 Ofen, 21 Messungen,
 * 714 Emissionsfelder) hat alle vier vorab definierten Traegheits-Schwellen ueberschritten.
 * `KilnForm` wird deshalb NUR gerendert, wenn die Messung aufgeklappt ist ODER schon einmal
 * aufgeklappt war (`hasBeenExpanded`) -- der Merker verhindert, dass beim erneuten Zuklappen
 * bereits eingegebene, ungespeicherte Werte aus dem uncontrolled-Formularbaum verschwinden
 * (T-19-26).
 *
 * D-19-Nachmessung (19-07-SUMMARY.md "Nachher"): zwei der vier Schwellen blieben nach dem
 * Lazy-Rendering allein weiterhin gerissen (Tipp-Latenz bei mehreren gleichzeitig geoeffneten
 * Messungen Ø 238 ms gegen 50 ms, Faktor 10,8x gegen die Brennstoffe-Baseline gegen erlaubte
 * 3x). Ursache (19-09-Plan): der bekannte Dirty-Flip-Owner-Reconcile (STATE.md 18-09) reconciled
 * bei jeder beruehrten Messung weiterhin ALLE aufgeklappten Karten mit, weil die Karte selbst
 * keinen Memoisierungs-Wrapper trug -- genau das Problem, das `BrennstoffeSection.tsx` bereits
 * ueber `memo()` mit einer Fehler-Signatur als Comparator loest. Der Plan-Constraint aus 19-07,
 * der `React.memo` "auf Verdacht" verbot, ist durch die Nutzerentscheidung vom 2026-07-23
 * aufgehoben, nachdem die Messung den Bedarf belegt hat. Die Karte traegt deshalb jetzt densel-
 * ben Mechanismus: der Comparator vergleicht `errorSig`/`disabled`/`expanded`/`kilnIdx`/
 * `measurementIdx` und ignoriert bewusst `form` (neue Referenz pro Render, aber ref-stabile
 * Methoden) sowie `onToggle`/`onDelete` (analog `onRemove` in `OtherFuelRow`,
 * BrennstoffeSection.tsx) -- die dahinterliegenden Closures bleiben ueber `kilnIdx`/
 * `measurementIdx` korrekt, da beide Werte Teil des Comparators sind.
 *
 * 19-09-Task-2-Nachmessung (`hasBeenExpanded` bleibt, siehe 19-09-SUMMARY.md): mit dem
 * Memo-Comparator allein (Testlauf ohne `hasBeenExpanded`, `KilnForm` fuer alle 21 Messungen
 * unbedingt gerendert) hielten Tipp-Latenz und Aufklappen die Schwellen bereits, aber der
 * Tab-Wechsel riss sie massiv (Ø 2508 ms > 1000 ms, da alle 714 Felder aller Messungen sofort
 * beim Tab-Wechsel gemountet werden muessen). Mit `hasBeenExpanded` UND Memo zusammen (Reihe A)
 * hielten alle vier Schwellen (Tab-Wechsel Ø 69,5 ms, Aufklappen 146,5 ms, Tipp-Latenz Ø 18-32 ms
 * je nach Anzahl offener Messungen). Die zwei Mechanismen federn also unterschiedliche Lasten ab:
 * `hasBeenExpanded` begrenzt die MOUNT-Kosten (wie viele der 714 Felder ueberhaupt im DOM
 * existieren), der Memo-Comparator begrenzt die RECONCILE-Kosten bereits gemounteter, aber nicht
 * betroffener Karten beim Tippen/Dirty-Flip. Beide bleiben bestehen.
 */
export const KilnMeasurementCard = memo(function KilnMeasurementCard({
  form,
  kilnIdx,
  measurementIdx,
  expanded,
  onToggle,
  onDelete,
  disabled,
}: KilnMeasurementCardProps) {
  const [hasBeenExpanded, setHasBeenExpanded] = useState(expanded);
  useEffect(() => {
    if (expanded) setHasBeenExpanded(true);
  }, [expanded]);

  return (
    // Strukturell identisch zur Ofen-Karte (SectionToggleRow variant="card"), nur weiss statt grau
    // (D-12, design/img_4.png). Border hebt die weisse Messung vom grauen Ofen-Grund ab.
    <Box
      style={{ border: '1px solid var(--mantine-color-vdzGray-2)', borderRadius: 'var(--mantine-radius-md)' }}
    >
      <SectionToggleRow
        variant="card"
        cardBg="var(--mantine-color-white)"
        title={<MeasurementTitle form={form} kilnIdx={kilnIdx} measurementIdx={measurementIdx} />}
        expanded={expanded}
        onToggle={onToggle}
        rightSection={
          onDelete && (
            <ActionIcon
              color="red"
              variant="white"
              onClick={onDelete}
              disabled={disabled}
              title="Messung entfernen"
              style={{ border: '1px solid var(--mantine-color-vdzGray-2)' }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          )
        }
      >
        {hasBeenExpanded && (
          <KilnForm
            form={form}
            fieldPrefix={`rotaryKilns.${kilnIdx}.measurements.${measurementIdx}`}
            disabled={disabled}
          />
        )}
      </SectionToggleRow>
    </Box>
  );
},
(prev, next) =>
  prev.errorSig === next.errorSig &&
  prev.disabled === next.disabled &&
  prev.expanded === next.expanded &&
  prev.kilnIdx === next.kilnIdx &&
  prev.measurementIdx === next.measurementIdx);
